import React, { Fragment, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationCircleIcon } from "@heroicons/react/outline";
import { cancelSubscription } from "../../services/subscribe.service";
import LoadingButtonIcon from "../../components/LoadingButtonIcon";
import { updateUser } from "../../redux/actions/authActions";
import { clearUserData, responseErrorHandler } from "./../../utils/common";
import { useNavigate } from "react-router-dom";

export default function Subscriptions() {
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [submittingCancelSubscription, setSubmittingCancelSubscription] =
    useState(false);

  const user = useSelector((state) => state.auth.user);
  const lang = useSelector((state) => state.app.lang);
  const dispatch = useDispatch();
  let navigate = useNavigate();

  const cancelButtonRef = useRef(null);

  const handleCancelSubscription = () => {
    setSubmittingCancelSubscription(true);
    cancelSubscription()
      .then((response) => {
        if (response.status === 200) {
          let updatedUser = user;
          updatedUser.subscriptions = [];
          dispatch(updateUser(updatedUser));
          setSubmittingCancelSubscription(false);
          setCancelModalOpen(false);
        }
      })
      .catch((error) => {
        if (error.response.status === 409) {
          let updatedUser = user;
          updatedUser.subscriptions = [];
          dispatch(updateUser(updatedUser));
          setSubmittingCancelSubscription(false);
          setCancelModalOpen(false);
        } else if (error.response.status === 401) {
          clearUserData();
          navigate("/login?logout=session-timeout");
        } else {
          responseErrorHandler(error, lang);
        }
      });
  };

  return (
    <>
      <Transition.Root show={cancelModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed z-10 inset-0 overflow-y-auto"
          onClose={setCancelModalOpen}
          initialFocus={cancelButtonRef}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-dark rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full sm:p-6">
                <div>
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                    <ExclamationCircleIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg leading-6 font-medium text-gray-100"
                    >
                      {lang === "en" ? "Cancel Subscription" : "إلغاء الإشتراك"}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-300">
                        {lang === "en"
                          ? "After cancelation you will not be able to play games!"
                          : "بعد إلغاء الإشتراك لن تتمكن من اللعب في شليل!"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm"
                    onClick={() => handleCancelSubscription()}
                  >
                    {submittingCancelSubscription ? (
                      <LoadingButtonIcon />
                    ) : lang === "en" ? (
                      "Cancel Subscription"
                    ) : (
                      "إلغاء اللإشتراك"
                    )}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-shilail-400 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => setCancelModalOpen(false)}
                    ref={cancelButtonRef}
                  >
                    {submittingCancelSubscription ? (
                      <LoadingButtonIcon />
                    ) : lang === "en" ? (
                      "Keep Subscription"
                    ) : (
                      "إبقاء الإشتراك"
                    )}
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
      <main className="w-full mx-auto pb-10 mt-6">
        <div className="lg:grid lg:grid-cols-12 lg:gap-x-5">
          {/* Payment details */}
          <div className="space-y-6 sm:px-6 lg:px-0 lg:col-span-12">
            {/* Plan */}
            <section aria-labelledby="plan-heading">
              <div className="sm:overflow-hidden">
                <div className="space-y-6">
                  <div>
                    <h2
                      id="plan-heading"
                      className="text-xl leading-6 font-medium text-white text-opacity-50"
                    >
                      {lang === "en" ? "Subscriptions" : "الإشتراكات"}
                    </h2>
                    <h5 className="text-sm leading-6 font-medium text-white text-opacity-20">
                      {lang === "en"
                        ? "Subscription informations."
                        : "ادارة بيانات الاشتراكات."}
                    </h5>
                  </div>
                </div>
              </div>
            </section>

            <section aria-labelledby="billing-history-heading">
              <div className="bg-dark pt-6 shadow sm:rounded-md sm:overflow-hidden">
                <div className="px-4 sm:px-6">
                  <h2
                    id="billing-history-heading"
                    className="text-lg leading-6 font-medium text-white text-opacity-50"
                  >
                    {lang === "en"
                      ? "Active subscriptions"
                      : "الإشتراكات الحالية"}
                  </h2>
                </div>
                {user.subscriptions.length > 0 ? (
                  <div className="mt-6 flex flex-col">
                    <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                      <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                        <div className="overflow-hidden border-t border-gray-800">
                          <table className="min-w-full divide-y divide-gray-800">
                            <thead className="bg-darker">
                              <tr>
                                <th
                                  scope="col"
                                  className="px-6 py-3 ltr:text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  {lang === "en"
                                    ? "Subscribed since"
                                    : "تاريخ الإشتراك"}
                                </th>
                                <th
                                  scope="col"
                                  className="px-6 py-3 ltr:text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  {lang === "en" ? "Plan" : "الخطة"}
                                </th>
                                <th
                                  scope="col"
                                  className="px-6 py-3 ltr:text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  {lang === "en" ? "Amount" : "السعر"}
                                </th>

                                <th
                                  scope="col"
                                  className="relative px-6 py-3 ltr:text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                                >
                                  <span className="sr-only">
                                    {lang === "en"
                                      ? "View receipt"
                                      : "عرض الفاتورة"}
                                  </span>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-dark divide-y divide-gray-200">
                              <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white text-opacity-50">
                                  {
                                    user.subscriptions[0].createdAt.split(
                                      "T",
                                    )[0]
                                  }
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {lang === "en"
                                    ? user.subscriptions[0].price.name_en
                                    : user.subscriptions[0].price.name_ar}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {user.subscriptions[0].amount}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={() => setCancelModalOpen(true)}
                                    type="button"
                                    className="text-white text-opacity-50 hover:text-white hover:text-opacity-80"
                                  >
                                    {lang === "en"
                                      ? "Cancel subscription"
                                      : "إلغاء الإشتراك"}
                                  </button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-6 flex flex-col">
                    <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                      <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                        <div className="overflow-hidden border-t border-gray-800 p-6 text-white text-opacity-50 text-center">
                          {lang === "en"
                            ? "No active subscriptions"
                            : "لا يوجد اشتراكات حالية"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}
